import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import StarEmpty from '../../../design-system/icons/core/star-empty.svg?component';
import StarFilled from '../../../design-system/icons/core/star-filled.svg?component';
import StarHalf from '../../../design-system/icons/core/star-half.svg?component';
import { RCIcon } from '../RCIcon';
import { RCTooltip } from '../RCTooltip';

interface IRCStarRatingProps {
  /** decimal rating from 0.0 to 1.0 */
  rating: number;
  /** Show count if provided. Returns null if count is 0. */
  count?: number;
  /** Increases width of each star to 2.6rem instead of 1.6rem */
  largeStars?: boolean;
  /** Specify to render star rating without a tooltip */
  hideTooltip?: boolean;
}

function getStarsCount(rating) {
  const ratingOutOf5 = Math.max(0, Math.min(rating, 1.0)) * 5.0;
  const roundedToTheNearestHalf = Math.round(ratingOutOf5 / 0.5) * 0.5;

  const fullStars = Math.floor(roundedToTheNearestHalf);
  const halfStars = roundedToTheNearestHalf > fullStars ? 1 : 0;
  const emptyStars = 5 - fullStars - halfStars;

  return {
    fullStars,
    halfStars,
    emptyStars,
    displayRating: ratingOutOf5.toLocaleString(undefined, { maximumFractionDigits: 2 }),
  };
}

export function RCStarRating({
  rating,
  count,
  largeStars,
  hideTooltip,
}: IRCStarRatingProps) {
  const classes = classNames(
    'rc-star-rating',
    { 'rc-star-rating--large': largeStars },
  );

  const {
    fullStars,
    halfStars,
    emptyStars,
    displayRating,
  } = getStarsCount(rating);

  if (count === 0) return null;

  const ratingText = I18n.t('commons.starRating.ratingText', { rating: displayRating });

  const starsContainer = (
    <div className="rc-star-rating__stars" aria-label={ratingText} role="img">
      {[...Array(fullStars)].map((_, index) => (
        <RCIcon key={index} svgComponent={StarFilled} />
      ))}
      {!!halfStars && (
        <RCIcon key={fullStars} svgComponent={StarHalf} />
      )}
      {[...Array(emptyStars)].map((_, index) => (
        <RCIcon key={fullStars + 1 + index} svgComponent={StarEmpty} />
      ))}
    </div>
  );

  return (
    <div className={classes}>
      {hideTooltip && starsContainer}
      {!hideTooltip && (
        <RCTooltip text={ratingText} type="plain">
          {starsContainer}
        </RCTooltip>
      )}

      {count !== undefined &&
        <div className="rc-star-rating__count">
          {`(${Math.round(count).toLocaleString()})`}
        </div>
      }
    </div>
  );
}
